import React, { useEffect, useState } from 'react';
import PWAPrompt from 'react-ios-pwa-prompt';
import { isIOS, isAndroid, isMobile } from 'react-device-detect';
import styled from 'styled-components';
import { getText, TEXT_KEY } from 'shared/utils/localization';

const AndroidPWAPrompt = ({ onInstall, onCancel }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [showManualInstructions, setShowManualInstructions] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) return;

    // Check if prompt was already dismissed
    const promptDismissed = localStorage.getItem('pwa-prompt-dismissed');
    const lastPromptTime = localStorage.getItem('pwa-prompt-last-shown');
    const now = Date.now();

    // Show prompt again after 7 days if dismissed
    if (promptDismissed && lastPromptTime) {
      const daysSinceLastPrompt = (now - parseInt(lastPromptTime)) / (1000 * 60 * 60 * 24);
      if (daysSinceLastPrompt < 7) return;
    }

    let promptTimeout;

    const handleBeforeInstallPrompt = e => {
      console.log('beforeinstallprompt event fired!', e);
      e.preventDefault();
      setDeferredPrompt(e);

      // Show custom prompt after delay
      promptTimeout = setTimeout(() => {
        setShowPrompt(true);
        localStorage.setItem('pwa-prompt-last-shown', now.toString());
      }, 2000);
    };

    // Listen for the beforeinstallprompt event
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Fallback for Android browsers that don't fire beforeinstallprompt
    // or when testing in Chrome DevTools
    if (isAndroid || (isMobile && !isIOS)) {
      promptTimeout = setTimeout(() => {
        // Only show manual instructions if beforeinstallprompt hasn't fired
        if (!deferredPrompt) {
          setShowManualInstructions(true);
          setShowPrompt(true);
          localStorage.setItem('pwa-prompt-last-shown', now.toString());
        }
      }, 3000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      if (promptTimeout) clearTimeout(promptTimeout);
    };
  }, [deferredPrompt]);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // Use the deferred prompt
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        onInstall && onInstall();
      }

      setDeferredPrompt(null);
      setShowPrompt(false);
    } else {
      // Show manual instructions
      setShowManualInstructions(true);
    }
  };

  const handleCancelClick = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', 'true');
    localStorage.setItem('pwa-prompt-last-shown', Date.now().toString());
    onCancel && onCancel();
  };

  if (!showPrompt) return null;

  return (
    <AndroidPromptContainer>
      <PromptHeader>
        <AppIcon src="/icons/icon-192.png" alt="App Icon" />
        <PromptTitle>{getText(TEXT_KEY.PWA_PROMPT_TITLE)}</PromptTitle>
      </PromptHeader>

      {showManualInstructions ? (
        <>
          <PromptText>{getText(TEXT_KEY.PWA_PROMPT_DESCRIPTION)}</PromptText>
          <InstructionsContainer>
            <InstructionStep>
              <StepNumber>1</StepNumber>
              <span>{getText(TEXT_KEY.PWA_PROMPT_STEP_1)}</span>
            </InstructionStep>
            <InstructionStep>
              <StepNumber>2</StepNumber>
              <span>{getText(TEXT_KEY.PWA_PROMPT_STEP_2)}</span>
            </InstructionStep>
            <InstructionStep>
              <StepNumber>3</StepNumber>
              <span>{getText(TEXT_KEY.PWA_PROMPT_STEP_3)}</span>
            </InstructionStep>
          </InstructionsContainer>
        </>
      ) : (
        <PromptText>{getText(TEXT_KEY.PWA_PROMPT_MANUAL_INSTALL)}</PromptText>
      )}

      <ButtonContainer>
        <CancelButton onClick={handleCancelClick}>
          {showManualInstructions
            ? getText(TEXT_KEY.PWA_PROMPT_GOT_IT_BUTTON)
            : getText(TEXT_KEY.PWA_PROMPT_NOT_NOW_BUTTON)}
        </CancelButton>
        {!showManualInstructions && (
          <InstallButton onClick={handleInstallClick}>{getText(TEXT_KEY.PWA_PROMPT_INSTALL_BUTTON)}</InstallButton>
        )}
      </ButtonContainer>
    </AndroidPromptContainer>
  );
};

const CrossPlatformPWAPrompt = () => {
  if (isIOS) return <PWAPrompt timesToShow={99} />;
  if (isAndroid || (isMobile && !isIOS)) return <AndroidPWAPrompt />;
  return null;
};

export default CrossPlatformPWAPrompt;

const AndroidPromptContainer = styled.div`
  position: fixed;
  bottom: 20px;
  left: 20px;
  right: 20px;
  border-radius: 12px;
  background: rgba(44, 44, 46, 0.98);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  padding: 20px;
  z-index: 999999999999;
  animation: slideUp 0.3s ease-out;

  @keyframes slideUp {
    from {
      transform: translateY(100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const PromptHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
`;

const AppIcon = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 10px;
  margin-right: 12px;
`;

const PromptTitle = styled.h3`
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  color: #ffffff;
`;

const PromptText = styled.p`
  margin: 0 0 10px 0;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.4;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const InstallButton = styled(Button)`
  background: #007aff;
  color: white;
`;

const CancelButton = styled(Button)`
  background: #f0f0f0;
  color: #333;
`;

const InstructionsContainer = styled.div`
  padding: 5px;
  margin-bottom: 16px;
`;

const InstructionStep = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);

  &:last-child {
    margin-bottom: 0;
  }
`;

const StepNumber = styled.span`
  background: #007aff;
  color: white;
  min-width: 24px;
  width: 24px;
  min-height: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
  line-height: 1;
  padding: 0;
`;

